import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  User,
  Mail,
  FileText,
  CreditCard,
  MapPin,
  Home,
  ArrowRight,
  CheckCircle,
  Sparkles
} from 'lucide-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { authAPI } from '../services/api';
import Input from '../modern-components/ui/Input';
import Textarea from '../modern-components/ui/Textarea';
import Button from '../modern-components/ui/Button';
import Card from '../modern-components/ui/Card';

const Register = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);

  // Load existing user data
  const existingUser = JSON.parse(localStorage.getItem('user') || '{}');
  const existingCustomer = existingUser?.customer || {};

  const formik = useFormik({
    initialValues: {
      company_name: existingCustomer.company_name || '',
      email: existingUser.email || '',
      gstin: existingCustomer.gstin || '',
      pan: existingCustomer.pan || '',
      address: existingCustomer.address || '',
      city: existingCustomer.city || '',
      state: existingCustomer.state || '',
      pincode: existingCustomer.pincode || '',
      contact_person: existingCustomer.contact_person || ''
    },
    validationSchema: Yup.object({
      company_name: Yup.string().required('Company name is required'),
      email: Yup.string().email('Invalid email').nullable(),
      gstin: Yup.string()
        .nullable()
        .test('gstin-format', 'Invalid GSTIN format', function(value) {
          if (!value || value === '') return true; // Allow empty
          return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value);
        }),
      pan: Yup.string()
        .nullable()
        .test('pan-format', 'Invalid PAN format', function(value) {
          if (!value || value === '') return true; // Allow empty
          return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value);
        }),
      pincode: Yup.string()
        .nullable()
        .test('pincode-format', 'Pincode must be 6 digits', function(value) {
          if (!value || value === '') return true; // Allow empty
          return /^[0-9]{6}$/.test(value);
        })
    }),
    onSubmit: async (values) => {
      setLoading(true);
      setError('');

      try {
        const response = await authAPI.registerCustomer(values);
        if (response.data.success) {
          // Update user in localStorage
          const user = JSON.parse(localStorage.getItem('user'));
          user.customer = response.data.data;
          if (values.email) {
            user.email = values.email;
          }
          localStorage.setItem('user', JSON.stringify(user));

          // Show success and redirect to home
          navigate('/', { replace: true });
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to update profile');
      } finally {
        setLoading(false);
      }
    }
  });

  const steps = [
    { number: 1, title: 'Company Info', description: 'Basic details' },
    { number: 2, title: 'Tax Details', description: 'GSTIN & PAN' },
    { number: 3, title: 'Address', description: 'Location info' }
  ];

  const nextStep = () => {
    // Validate current step fields before proceeding
    const step1Fields = ['company_name', 'contact_person', 'email'];
    const step2Fields = ['gstin', 'pan'];

    if (currentStep === 1) {
      const hasErrors = step1Fields.some(field => {
        formik.setFieldTouched(field, true);
        return formik.errors[field];
      });
      if (!formik.values.company_name) {
        return;
      }
    } else if (currentStep === 2) {
      step2Fields.forEach(field => formik.setFieldTouched(field, true));
    }

    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50 flex">
      {/* Left Panel - Welcome */}
      <div className="hidden lg:flex lg:w-2/5 bg-gradient-to-br from-primary-800 via-primary-700 to-primary-900 p-12 flex-col justify-between relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-secondary-500 rounded-full opacity-10 -mr-48 -mt-48"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary-500 rounded-full opacity-10 -ml-32 -mb-32"></div>

        {/* Logo and Branding */}
        <div className="relative z-10">
          <div className="flex items-center space-x-3 mb-8">
            <div className="w-14 h-14 bg-secondary-500 rounded-xl flex items-center justify-center shadow-lg">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Loha Mart</h1>
              <p className="text-secondary-300 text-sm">Industrial Marketplace</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
                You're Almost There!
              </h2>
              <p className="text-lg text-gray-300 leading-relaxed">
                Complete your business profile to unlock access to premium industrial materials and competitive pricing.
              </p>
            </div>

            {/* Benefits List */}
            <div className="space-y-4 mt-8">
              <div className="flex items-start space-x-3">
                <div className="bg-secondary-500 bg-opacity-20 p-2 rounded-lg mt-1">
                  <CheckCircle className="w-5 h-5 text-secondary-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Instant Quote Access</h3>
                  <p className="text-gray-300 text-sm">Get real-time pricing for bulk orders</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-secondary-500 bg-opacity-20 p-2 rounded-lg mt-1">
                  <CheckCircle className="w-5 h-5 text-secondary-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Credit Facilities</h3>
                  <p className="text-gray-300 text-sm">Flexible payment terms for businesses</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-secondary-500 bg-opacity-20 p-2 rounded-lg mt-1">
                  <CheckCircle className="w-5 h-5 text-secondary-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Priority Support</h3>
                  <p className="text-gray-300 text-sm">Dedicated account manager for your needs</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Quote */}
        <div className="relative z-10 border-l-4 border-secondary-500 pl-4">
          <p className="text-white text-lg italic mb-2">
            "Loha Mart has transformed how we source construction materials. The platform is reliable and efficient."
          </p>
          <p className="text-secondary-300 text-sm">- Rajesh Kumar, ABC Constructions</p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12">
        <div className="w-full max-w-2xl">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img src="/logo.png" alt="Loha Mart" className="h-12 w-auto object-contain" />
          </div>

          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-8">
              {steps.map((step, index) => (
                <div key={step.number} className="flex items-center flex-1">
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                        currentStep >= step.number
                          ? 'bg-gradient-to-br from-primary-600 to-secondary-600 text-white shadow-lg scale-110'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {currentStep > step.number ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        step.number
                      )}
                    </div>
                    <div className="text-center mt-2">
                      <p
                        className={`text-xs font-semibold ${
                          currentStep >= step.number ? 'text-primary-700' : 'text-gray-500'
                        }`}
                      >
                        {step.title}
                      </p>
                      <p className="text-xs text-gray-400 hidden sm:block">{step.description}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="flex-1 h-1 mx-2 -mt-8">
                      <div
                        className={`h-full rounded transition-all duration-300 ${
                          currentStep > step.number ? 'bg-gradient-to-r from-primary-600 to-secondary-600' : 'bg-gray-200'
                        }`}
                      ></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Form Card */}
          <Card className="shadow-xl animate-fade-in">
            <div className="mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Complete Your Profile
              </h2>
              <p className="text-gray-600">
                {currentStep === 1 && 'Tell us about your company'}
                {currentStep === 2 && 'Provide your tax identification details'}
                {currentStep === 3 && 'Where is your business located?'}
              </p>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg animate-slide-down">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={formik.handleSubmit}>
              {/* Step 1: Company Info */}
              {currentStep === 1 && (
                <div className="space-y-5 animate-fade-in">
                  <Input
                    label="Company Name"
                    type="text"
                    id="company_name"
                    name="company_name"
                    placeholder="Enter your company name"
                    value={formik.values.company_name}
                    onChange={formik.handleChange}
                    error={formik.touched.company_name && formik.errors.company_name}
                    leftIcon={<Building2 className="w-5 h-5" />}
                    disabled={loading}
                    required
                  />

                  <Input
                    label="Contact Person"
                    type="text"
                    id="contact_person"
                    name="contact_person"
                    placeholder="Name of the primary contact person"
                    value={formik.values.contact_person}
                    onChange={formik.handleChange}
                    error={formik.touched.contact_person && formik.errors.contact_person}
                    leftIcon={<User className="w-5 h-5" />}
                    disabled={loading}
                  />

                  <Input
                    label="Email Address"
                    type="email"
                    id="email"
                    name="email"
                    placeholder="company@example.com"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={formik.touched.email && formik.errors.email}
                    leftIcon={<Mail className="w-5 h-5" />}
                    disabled={loading}
                  />
                </div>
              )}

              {/* Step 2: Tax Details */}
              {currentStep === 2 && (
                <div className="space-y-5 animate-fade-in">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                    <p className="text-sm text-blue-800 flex items-start space-x-2">
                      <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Tax details are optional but help us provide better services and credit facilities.</span>
                    </p>
                  </div>

                  <Input
                    label="GSTIN"
                    type="text"
                    id="gstin"
                    name="gstin"
                    placeholder="22AAAAA0000A1Z5"
                    value={formik.values.gstin}
                    onChange={formik.handleChange}
                    error={formik.touched.gstin && formik.errors.gstin}
                    leftIcon={<FileText className="w-5 h-5" />}
                    disabled={loading}
                  />

                  <Input
                    label="PAN"
                    type="text"
                    id="pan"
                    name="pan"
                    placeholder="ABCDE1234F"
                    value={formik.values.pan}
                    onChange={formik.handleChange}
                    error={formik.touched.pan && formik.errors.pan}
                    leftIcon={<CreditCard className="w-5 h-5" />}
                    disabled={loading}
                  />
                </div>
              )}

              {/* Step 3: Address */}
              {currentStep === 3 && (
                <div className="space-y-5 animate-fade-in">
                  <Textarea
                    label="Address"
                    id="address"
                    name="address"
                    placeholder="Enter your complete business address"
                    value={formik.values.address}
                    onChange={formik.handleChange}
                    error={formik.touched.address && formik.errors.address}
                    disabled={loading}
                    rows={3}
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Input
                      label="City"
                      type="text"
                      id="city"
                      name="city"
                      placeholder="City name"
                      value={formik.values.city}
                      onChange={formik.handleChange}
                      error={formik.touched.city && formik.errors.city}
                      leftIcon={<Home className="w-5 h-5" />}
                      disabled={loading}
                    />

                    <Input
                      label="State"
                      type="text"
                      id="state"
                      name="state"
                      placeholder="State name"
                      value={formik.values.state}
                      onChange={formik.handleChange}
                      error={formik.touched.state && formik.errors.state}
                      leftIcon={<MapPin className="w-5 h-5" />}
                      disabled={loading}
                    />
                  </div>

                  <Input
                    label="Pincode"
                    type="text"
                    id="pincode"
                    name="pincode"
                    placeholder="123456"
                    value={formik.values.pincode}
                    onChange={formik.handleChange}
                    error={formik.touched.pincode && formik.errors.pincode}
                    leftIcon={<MapPin className="w-5 h-5" />}
                    disabled={loading}
                    maxLength={6}
                  />
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                {currentStep > 1 ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    disabled={loading}
                  >
                    Previous
                  </Button>
                ) : (
                  <div></div>
                )}

                {currentStep < 3 ? (
                  <Button
                    type="button"
                    variant="primary"
                    onClick={nextStep}
                    rightIcon={<ArrowRight className="w-5 h-5" />}
                  >
                    Next Step
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    variant="primary"
                    loading={loading}
                    rightIcon={<CheckCircle className="w-5 h-5" />}
                  >
                    Complete Registration
                  </Button>
                )}
              </div>
            </form>
          </Card>

          {/* Help Text */}
          <p className="text-center text-sm text-gray-600 mt-6">
            Need assistance? Contact us at{' '}
            <a href="tel:+911234567890" className="text-primary-600 hover:text-primary-700 font-medium">
              +91 123 456 7890
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
